/**
 * MessageSection component
 * 
 * This component handles the chat interface, including:
 * - Displaying messages between users
 * - Sending new messages
 * - Contact limit validation for employers
 * - Infinite scrolling for message history
 */
import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";

// Material UI imports
import {
  Box,
  Typography,
  IconButton,
  Paper,
  Avatar,
  InputBase,
} from "@mui/material";
import Papers from '@mui/material/Paper';
import SendIcon from '@mui/icons-material/Send';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { styled } from "@mui/system";

// Third-party imports
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import moment from "moment/moment";
import InfiniteScroll from "react-infinite-scroll-component";

// Service imports
import { createChatroom, getMessages, sendMessage, processMessageForNotification, processJobseekerMessage } from "../../../../services/chatService";
import { checkContactApplicantPermission, recordContactApplicant } from "../../../../services/contactApplicantService";

// Component imports
import GlobalModal from "../../Popups/GlobalModal";

// -------------------------
// Styled Components
// -------------------------

const StyledChatArea = styled(Box)(({ theme }) => ({
  flex: 1,
  display: "flex",
  background: "#ffffff",
  flexDirection: "column",
  borderRadius: 5,
  height: "100%",
}));

// Extract the message bubble to its own component for focused rerenders
const MessageBubble = React.memo(({ msg, isOwn, userId }) => {
  return (
    <Box
      display="flex"
      flexDirection="column"
      marginX={3}
      alignItems={msg.senderId === userId ? "flex-end" : "flex-start"}
    >
      <Paper
        sx={{
          padding: "10px 15px",
          margin: "8px 0",
          maxWidth: "70%",
          width: "fit-content",
          background: isOwn ? "#0364b2" : "#e0e0e0",
          borderRadius: "12px",
          alignSelf: isOwn ? "flex-end" : "flex-start"
        }}
      >
        <Typography color={isOwn ? "#ffffff" : "#000000"}>
          {msg.messageContent}
        </Typography>
        <Typography
          variant="caption"
          sx={{ color: isOwn ? "#f2f4f5" : "#000000" }}
        >
          {moment(msg.createdAt).format('LT')}
        </Typography>
      </Paper>
    </Box>
  );
}, (prevProps, nextProps) => {
  // Only rerender if message content or ownership changes
  return (
    prevProps.msg.id === nextProps.msg.id &&
    prevProps.msg.messageContent === nextProps.msg.messageContent &&
    prevProps.isOwn === nextProps.isOwn
  );
});

const StyledInputArea = styled(Box)({
  padding: "10px 20px",
  flex: "0 0 auto",
  background: "#ffffff",
  borderTop: "1px solid #f0f0f0",
});

// Memoized MessageList component to prevent rerenders
const MessageList = React.memo(({ messages, user, loadMoreMessages, hasMore }) => {
  return (
    <Box style={{
      flex: "1 1 auto",
      overflow: "auto",
      display: "flex",
      flexDirection: "column-reverse",
      paddingBottom: "10px"
    }}>
      <div
        id="scrollableDiv"
        style={{
          height: '100%',
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column-reverse',
        }}
      >
        <InfiniteScroll
          dataLength={messages.length}
          next={loadMoreMessages}
          style={{ display: 'flex', flexDirection: 'column-reverse' }}
          inverse={true}
          hasMore={hasMore}
          scrollableTarget="scrollableDiv"
        >
          {messages.map((msg, index) => (
            <MessageBubble
              key={msg.id || index.toString()}
              msg={msg}
              isOwn={msg.senderId === user?.id}
              userId={user?.id}
            />
          ))}
        </InfiniteScroll>
      </div>
    </Box>
  );
});

// Memoized ChatHeader component
const ChatHeader = React.memo(({ selectedChatroom, setSelectedChatroom, navigateToProfile }) => {
  if (!selectedChatroom) return null;

  return (
    <Box sx={{
      padding: "10px 20px",
      display: "flex",
      alignItems: 'center',
      gap: '10px',
      borderBottom: '1px solid #e0e0e0',
      color: "#fff",
      flex: "0 0 auto"
    }}>
      {setSelectedChatroom && (
        <ArrowBackIcon
          onClick={() => setSelectedChatroom(null)}
          style={{ color: '#000000', cursor: 'pointer' }}
        />
      )}

      <Avatar
        src={`${process.env.REACT_APP_UPLOADS_PATH + selectedChatroom.avatar}`}
        alt={selectedChatroom.name}
      />

      <Typography
        variant="h6"
        onClick={navigateToProfile}
        style={{ cursor: "pointer" }}
      >
        {selectedChatroom?.name}
      </Typography>
    </Box>
  );
}, (prevProps, nextProps) => {
  // Only rerender if selected chatroom changes
  return (
    prevProps.selectedChatroom?.id === nextProps.selectedChatroom?.id &&
    prevProps.selectedChatroom?.name === nextProps.selectedChatroom?.name &&
    prevProps.selectedChatroom?.avatar === nextProps.selectedChatroom?.avatar
  );
});

// Memoized MessageInput component
const MessageInput = React.memo(({ message, setMessage, handleKeyDown, handleMessageSend, t }) => {
  return (
    <StyledInputArea>
      <Papers
        component="form"
        style={{
          display: "flex",
          flexDirection: 'row',
          backgroundColor: "white",
          border: '1px solid #000000',
          width: '100%',
          padding: '5px 15px'
        }}
        onSubmit={(e) => e.preventDefault()}
      >
        <InputBase
          value={message}
          placeholder={t('chat.chat_section.placeholder')}
          onKeyDown={handleKeyDown}
          onChange={(e) => setMessage(e.target.value)}
          style={{ color: '#000000' }}
          fullWidth
        />

        <IconButton
          onClick={handleMessageSend}
          type="button"
          aria-label={t('chat.chat_section.send_button')}
        >
          <SendIcon style={{ color: '#000000' }} />
        </IconButton>
      </Papers>
    </StyledInputArea>
  );
}, (prevProps, nextProps) => {
  // Only rerender if message changes
  return prevProps.message === nextProps.message;
});

// -------------------------
// Main Component
// -------------------------

/**
 * MessageSection Component
 * 
 * @param {Object} props
 * @param {Object} props.selectedChatroom - The currently selected chatroom
 * @param {Object} props.socket - Socket.io connection
 * @param {Function} props.setSelectedChatroom - Function to change selected chatroom
 * @param {Object} props.recievedMsg - Received message from socket
 * @param {Function} props.onMessageSent - Callback function when a message is sent
 */
const MessageSection = React.memo(({ selectedChatroom, socket, setSelectedChatroom, recievedMsg, onMessageSent }) => {
  // Hooks
  const { t } = useTranslation();
  const user = useSelector((state) => state.user.user);
  const userToken = useSelector((state) => state.user.token);
  const features = useSelector((state) => state.user.features);

  // State
  const [message, setMessage] = useState("");
  const [messages, setMessages] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeMessage, setUpgradeMessage] = useState("");

  // Refs
  const prevSelectedChatroomRef = useRef(null);

  // -------------------------
  // Effects
  // -------------------------

  /**
   * Handle received messages from socket
   */
  useEffect(() => {
    if (!recievedMsg || !recievedMsg.roomId || recievedMsg.roomId !== selectedChatroom?.id) {
      return;
    }

    // Check if this is our own message that we already added optimistically
    if (recievedMsg.tempId && recievedMsg.senderId === user?.id) {
      // This is our message coming back through the socket
      // We already added it to the UI, so skip it
      return;
    }

    // Add the received message to our messages list
    setMessages(prevMessages => [recievedMsg, ...prevMessages]);
  }, [recievedMsg, selectedChatroom, user?.id]);

  /**
   * Load messages when chatroom is selected
   */
  useEffect(() => {
    // Skip if no selected chatroom or user
    if (!selectedChatroom?.id || !user?.id) return;

    // Skip if the selected chatroom hasn't changed
    if (prevSelectedChatroomRef.current?.id === selectedChatroom?.id) return;

    // Update the previous chatroom ref
    prevSelectedChatroomRef.current = selectedChatroom;

    // Reset state for new chatroom
    setMessages([]);
    setPage(1);
    setHasMore(true);

    // Fetch messages
    fetchChatroomMessages();
  }, [selectedChatroom?.id, user?.id]);

  // -------------------------
  // Helper Functions
  // -------------------------

  /**
   * Fetch messages for the selected chatroom
   */
  const fetchChatroomMessages = useCallback(async () => {
    if (!selectedChatroom?.id || !user?.id) return;

    const employerId = user.account_type === 'employer' ? user?.id : selectedChatroom?.chatroom?.id;
    const jobSeekerId = user.account_type !== 'employer' ? user?.id : selectedChatroom?.chatroom?.id;

    try {
      const chatMessages = await getMessages(selectedChatroom?.id, 20, 0);
      setMessages(chatMessages);
    } catch (error) {
      if (error.response?.status === 404) {
        await createChatroom(employerId, jobSeekerId);
      }
    }
  }, [selectedChatroom, user]);

  /**
   * Extract jobseeker ID from chatroom object
   * @returns {number|null} The jobseeker ID or null if not found
   */
  const getJobseekerId = useCallback(() => {
    console.log('Getting jobseeker ID from chatroom:', selectedChatroom);

    // Try different paths to find the jobseeker ID
    const paths = [
      selectedChatroom?.chatroom?.jobSeekerId,
      selectedChatroom?.chatroom?.chatroom?.jobSeekerId,
      selectedChatroom?.jobSeekerId,
      selectedChatroom?.chatroom?.id, // If the chatroom ID is actually the jobseeker ID
      selectedChatroom?.id // Last resort
    ];

    // Log all possible paths
    console.log('Possible jobseeker ID paths:', paths);

    // Return the first non-null value
    const jobseekerId = paths.find(id => id !== undefined && id !== null);
    console.log('Selected jobseeker ID:', jobseekerId);

    return jobseekerId || null;
  }, [selectedChatroom]);

  /**
   * Check if employer has permission to contact the jobseeker
   * @returns {Promise<boolean>} Whether contact is allowed
   */
  const checkContactPermission = useCallback(async () => {
    if (user?.account_type !== 'employer' || !features || !userToken) {
      return true; // Only check limits for employers
    }

    const jobseekerId = getJobseekerId();
    if (!jobseekerId) return true;

    try {
      // Check if the employer can contact this applicant
      const result = await checkContactApplicantPermission({
        employerId: user.id,
        jobseekerId: jobseekerId,
        features,
        token: userToken
      });

      if (!result.canContact) {
        handleContactLimitReached(result);
        return false;
      }

      // Record the contact if not already contacted
      if (!result.alreadyContacted) {
        await recordContactApplicant({
          employerId: user.id,
          jobseekerId: jobseekerId,
          token: userToken
        });
      }

      return true;
    } catch (error) {
      console.error('Error checking contact limits:', error);
      return true; // Allow sending on error to prevent blocking communication
    }
  }, [features, getJobseekerId, user?.account_type, user?.id, userToken]);

  /**
   * Handle the case when contact limit is reached
   * @param {Object} result - The result from contact permission check
   */
  const handleContactLimitReached = useCallback((result) => {
    if (result.reason === 'MONTHLY_LIMIT_REACHED') {
      // Use translation with fallback message
      const fallbackMessage = `You've reached your monthly limit of ${features?.contactApplicant} applicant contacts. Please upgrade your subscription to contact more applicants.`;
      const translatedMessage = t('general.UI.contact_applicant_limit_reached', {
        limit: features?.contactApplicant,
        defaultValue: fallbackMessage
      });
      setUpgradeMessage(translatedMessage);
    } else {
      setUpgradeMessage(t('upgrade.subscription_required_for_contacts'));
    }

    setShowUpgradeModal(true);
  }, [features, t]);

  /**
   * Send a message to the current chatroom
   */
  const handleMessageSend = useCallback(async () => {
    if (message.trim() === "" || !selectedChatroom?.id) return;

    // Check contact limits for employers
    const canContact = await checkContactPermission();
    if (!canContact) return;

    // Generate a temporary ID for the message to track it
    const tempMessageId = `temp-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;

    // Create current timestamp
    const currentTime = new Date();
    const isoTimestamp = currentTime.toISOString();

    // Format the new message
    const newMessage = {
      senderId: user?.id,
      text: message,
      messageContent: message,
      isOwn: true,
      timestamp: isoTimestamp,
      createdAt: isoTimestamp,
      id: tempMessageId, // Add an ID to identify this message
      roomId: selectedChatroom.id, // Add roomId for chatroom identification
    };

    // Clear input and send message
    const messageToSend = message;
    setMessage("");

    try {
      // Optimistically add message to UI
      setMessages(prevMessages => [
        {
          ...newMessage,
          messageContent: messageToSend,
          createdAt: isoTimestamp
        },
        ...prevMessages
      ]);

      // Notify parent component about the new message
      if (onMessageSent) {
        onMessageSent({
          roomId: selectedChatroom.id,
          message: messageToSend,
          messageContent: messageToSend,
          timestamp: isoTimestamp,
          createdAt: isoTimestamp
        });
      }

      // Send to server
      await sendMessage(selectedChatroom?.id, user?.id, messageToSend);

      // Notify other users via socket
      const messageObj = {
        roomId: selectedChatroom.id,
        message: messageToSend,
        senderId: user.id,
        timestamp: isoTimestamp,
        createdAt: isoTimestamp,
        tempId: tempMessageId, // Send the temp ID with the socket message
        ...newMessage
      };

      socket.emit('sendMessage', { ...messageObj });

      // Process message for notification if sent from employer to jobseeker
      if (user.account_type === 'employer') {
        // Get jobseeker info from the chatroom
        const jobseekerId = getJobseekerId();
        console.log('Sending message as employer, jobseekerId:', jobseekerId, 'selectedChatroom:', selectedChatroom);

        if (jobseekerId) {
          // Process message for notification
          processMessageForNotification(
            messageObj,
            selectedChatroom,
            user,
            { id: jobseekerId },
            null // No job info for now
          ).catch(err => console.error('Error processing message for notification:', err));
        } else {
          console.error('Could not determine jobseeker ID from chatroom:', selectedChatroom);
        }
      } else if (user.account_type === 'jobseeker') {
        // Process message from jobseeker to employer
        const employerId = selectedChatroom?.chatroom?.chatroom?.employerId || selectedChatroom?.chatroom?.id;
         if (employerId) {
          // Process message for notification to employer
          processJobseekerMessage(
            messageObj,
            selectedChatroom,
            { id: employerId },
            user,
            null // No job info for now
          ).catch(err => console.error('Error processing jobseeker message for notification:', err));
        } else {
          console.error('Could not determine employer ID from chatroom:', selectedChatroom);
        }
      }
    } catch (error) {
      console.error('Error sending message:', error);
      // Restore message in input if sending failed
      setMessage(messageToSend);

      // Remove the optimistically added message
      setMessages(prevMessages => prevMessages.filter(msg => msg.id !== tempMessageId));
    }
  }, [message, selectedChatroom, checkContactPermission, user, socket, onMessageSent, getJobseekerId]);

  /**
   * Load more messages for infinite scroll
   */
  const loadMoreMessages = useCallback(async () => {
    if (!hasMore || !selectedChatroom?.id) return;

    const offset = page * 20;
    try {
      const newMessages = await getMessages(selectedChatroom?.id, 20, offset);

      if (newMessages.length === 0) {
        setHasMore(false);
      } else {
        setMessages((prevMessages) => [...prevMessages, ...newMessages]);
        setPage(page + 1);
      }
    } catch (error) {
      console.error('Error loading more messages:', error);
      setHasMore(false);
    }
  }, [hasMore, page, selectedChatroom?.id]);

  /**
   * Navigate to the profile of the chat partner
   */
  const navigateToProfile = useCallback(() => {
    if (!selectedChatroom?.chatroom?.slug) return;

    if (user.account_type === 'jobseeker') {
      window.location.href = `${'/employer'}s/${selectedChatroom.chatroom?.slug}`;
    } else {
      window.location.href = `${'/jobseekers'}/${selectedChatroom.chatroom?.slug}`;
    }
  }, [selectedChatroom?.chatroom?.slug, user.account_type]);

  /**
   * Handle keydown event for message input
   */
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleMessageSend();
    }
  }, [handleMessageSend]);

  // Memoize messages array to prevent unnecessary rerenders
  const memoizedMessages = useMemo(() => messages, [messages]);
  const memoizedHasMore = useMemo(() => hasMore, [hasMore]);

  // -------------------------
  // Main Render
  // -------------------------

  return (
    <>
      <StyledChatArea>
        {selectedChatroom?.id ? (
          <>
            <ChatHeader
              selectedChatroom={selectedChatroom}
              setSelectedChatroom={setSelectedChatroom}
              navigateToProfile={navigateToProfile}
            />
            <Box sx={{
              display: "flex",
              flexDirection: "column",
              flex: 1,
              overflow: "hidden"
            }}>
              <MessageList
                messages={memoizedMessages}
                user={user}
                loadMoreMessages={loadMoreMessages}
                hasMore={memoizedHasMore}
              />
              <MessageInput
                message={message}
                setMessage={setMessage}
                handleKeyDown={handleKeyDown}
                handleMessageSend={handleMessageSend}
                t={t}
              />
            </Box>
          </>
        ) : (
          <Typography
            variant="h6"
            sx={{
              display: 'flex',
              height: '100%',
              justifyContent: 'center',
              alignItems: "center"
            }}
          >
            {t('chat.chat_section.select_chat')}
          </Typography>
        )}
      </StyledChatArea>

      <GlobalModal
        show={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        title={t('upgrade.modal_title')}
        message={upgradeMessage}
        primaryButtonText={t('upgrade.upgrade_now')}
        secondaryButtonText={t('general.UI.cancel')}
        navigateTo="/subscriptions"
        secondaryButtonAction={() => setShowUpgradeModal(false)}
      />
    </>
  );
}, (prevProps, nextProps) => {
  // Only rerender when necessary
  if (prevProps.selectedChatroom?.id !== nextProps.selectedChatroom?.id) {
    return false; // Selected chatroom changed, need to rerender
  }

  if (prevProps.recievedMsg !== nextProps.recievedMsg) {
    // Check if the received message is for this chatroom
    if (nextProps.recievedMsg?.roomId === nextProps.selectedChatroom?.id) {
      return false; // New message for this chatroom, need to rerender
    }
  }

  // No important changes, don't rerender
  return true;
});

export default MessageSection;
